// The sale plane: while the video loops are on sale (SHOP.sale, site/js/ui/shop.js) a small plane tows a banner with
// the offer across the sky, right to left, so the words read from the plane backwards. The first pass sets off FIRST_MS
// after the page opened, then one every EVERY_MS. A link over it (#planeSpot, site/js/app.js) opens the shop. Where it
// is depends on the page's clock (a requestAnimationFrame time, ms) and on what the screen shows of the scene
// (display.view): it crosses a phone's crop as it does a wide screen, below the city title. It moves and ripples in
// fractions of a scene pixel, a step of the screen's own pixels every frame (display.sprites), so it glides at the
// screen's 60 frames a second where a scene-pixel step would tick along at SPEED steps a second. It is drawn straight
// onto the screen after the scene, never into a scene (PNG, MP4 and the mini player stay clean). Tested in
// tests/plane.test.mjs.
import { TAU, makeHalo, text, textWidth } from '../engine/core.js';

export const FIRST_MS = 30_000;     // the first pass
export const EVERY_MS = 300_000;    // then one every five minutes
export const SPEED = 28;            // scene pixels a second
const PROP_MS = 60, BEACON_EVERY = 1400, BEACON_MS = 180;
// the cloth's ripple: a wave WAVE_PX long runs down it every WAVE_MS, WAVE_AMP rows up and down once it is WAVE_RAMP
// columns clear of the pole, which holds its end still
export const WAVE_MS = 1800, WAVE_AMP = 1.25;
const WAVE_PX = 44, WAVE_RAMP = 16;

// Faces left, 20×9; the rope leaves the tail's end on row TOW, and the propeller turns in column 0. c canopy, n cowl,
// f fuselage, r stripe, w wing, d belly, t fin, g gear, o wheels
export const ART = [
  '..................tt',
  '.......ccc.......ttt',
  '......ccccf.....tttt',
  '.nnfffffffffffffffff',
  '.nnffrrrrrrrrrrrffff',
  '..nfffwwwwwwwwfff...',
  '....dddwwwwwwdd.....',
  '......g....g........',
  '......o....o........',
];
export const TOW = 4;
const FIN = { x: 18, y: 0 };        // the beacon, on the fin's tip
const SIZE = { w: ART[0].length, h: ART.length };
const ROPE = 6;                     // from the tail to the banner's pole
// the cloth: PAD pixels each side of the words, two rows above and below them, then a swallowtail TAIL pixels long
const PAD = 3, CLOTH_H = 9, TAIL = 2, HALF = CLOTH_H >> 1;
const COLOURS = {
  c: '#9fd8ef', n: '#4a4458', f: '#f2c14e', r: '#d0473c', w: '#fbe9b0', d: '#b98a2c', t: '#f2c14e', g: '#767e98', o: '#3f4458',
  prop: '#b4bccf', beacon: '#ff5a4a', rope: '#8790ab', pole: '#3f4458', cloth: '#f6f0de', hem: '#c9bb98', ink: '#15161f',
};

const clothWidth = words => PAD + textWidth(words) + PAD + TAIL;
/** How long the plane, its rope and the banner saying `words` are, nose to swallowtail, in scene pixels. */
export const trainWidth = words => SIZE.w + ROPE + 1 + clothWidth(words);

/** How far into a pass the page is at `now` (ms since the page opened): ms since it set off, or -1 before the first. */
export const passAt = now => (now < FIRST_MS ? -1 : (now - FIRST_MS) % EVERY_MS);

/**
 * The row the rope runs along. `view` is display.view's: the scene's columns [x, x + w) and rows [y, y + h) on screen,
 * shown in the CSS-pixel box { left, top, width, height }. `clear`: CSS px from the top of the page the city title
 * covers. The banner flies just under that, and never lower than two fifths of the way down.
 */
export function planeRow(view, clear = 0) {
  const covered = Math.max(0, Math.ceil((clear - view.top) * view.w / view.width));
  return Math.round(view.y) + Math.min(covered + HALF + 6, Math.floor(view.h * 0.4));
}

/**
 * The plane at `now` over `view` (planeRow's): { x, y, words, ms, still } (x: the nose's column, to a fraction of a
 * pixel; y: the rope's row; ms into the pass), or null between passes. It comes in at the view's right-hand edge and
 * is gone once the banner's end has left on the left. `still` (prefers-reduced-motion): it crosses without the
 * propeller's flicker, the beacon's blink or the cloth's ripple.
 */
export function planePose(now, view, { words, clear = 0, still = false }) {
  const ms = passAt(now);
  if (ms < 0) return null;
  const x = view.x + view.w - ms * SPEED / 1000;
  if (x + trainWidth(words) <= view.x) return null;
  return { x, y: planeRow(view, clear), words, ms, still };
}

/** The scene rect of the plane and its banner at `pose`, the ripple included, for display.spot. */
export function planeRect(pose) {
  const up = HALF + Math.ceil(WAVE_AMP);
  return { x: pose.x, y: pose.y - up, w: trainWidth(pose.words), h: 2 * up + 1 };
}

/**
 * How many rows (to a fraction) column `c` of the cloth is off the rope's row `ms` into a pass. Next-door columns are
 * never a quarter of a row apart, so every letter keeps its shape.
 */
export const liftAt = (c, ms) => WAVE_AMP * Math.min(1, c / WAVE_RAMP) * Math.sin(TAU * (c / WAVE_PX - ms / WAVE_MS));

let art = null;                     // { planes, halo, rig, words, cloth }: drawn on first use, the cloth again for new words
function canvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return [c, c.getContext('2d')];
}
// the plane with its propeller's blur (a long faint stroke, or a short one) and its beacon lit or not
function paintPlane(long, lit) {
  const [c, x] = canvas(SIZE.w, SIZE.h);
  ART.forEach((row, j) => [...row].forEach((ch, i) => {
    if (!COLOURS[ch]) return;
    x.fillStyle = COLOURS[ch];
    x.fillRect(i, j, 1, 1);
  }));
  x.fillStyle = COLOURS.prop;
  x.globalAlpha = long ? 0.45 : 0.8;
  x.fillRect(0, TOW - (long ? 2 : 1), 1, long ? 5 : 3);
  x.globalAlpha = 1;
  if (lit) {
    x.fillStyle = COLOURS.beacon;
    x.fillRect(FIN.x, FIN.y, 1, 1);
  }
  return c;
}
// the rope and the pole it holds
function paintRig() {
  const [c, x] = canvas(ROPE + 1, CLOTH_H);
  x.fillStyle = COLOURS.rope;
  x.fillRect(0, HALF, ROPE, 1);
  x.fillStyle = COLOURS.pole;
  x.fillRect(ROPE, 0, 1, CLOTH_H);
  return c;
}
// dark words on pale cloth with a shaded hem; the first word, the offer's figure, in red
function paintCloth(words) {
  const w = clothWidth(words), [c, x] = canvas(w, CLOTH_H);
  x.fillStyle = COLOURS.cloth;
  x.fillRect(0, 0, w, CLOTH_H);
  x.fillStyle = COLOURS.hem;
  x.fillRect(0, CLOTH_H - 1, w, 1);
  x.clearRect(w - 1, 2, 1, CLOTH_H - 4);              // the swallowtail's notch
  x.clearRect(w - 2, 3, 1, CLOTH_H - 6);
  text(x, words, PAD, 2, COLOURS.ink);
  text(x, words.split(' ')[0], PAD, 2, COLOURS.r);
  return c;
}

/**
 * Draws `pose` (planePose) with `put` (display.sprites', its origin at the pose's x, y): the plane, the beacon's glow,
 * the rope, and the banner a column at a time, each at its own height.
 */
export function drawPlane(put, pose) {
  art ??= { planes: [0, 1, 2, 3].map(i => paintPlane(i < 2, i % 2 === 1)), halo: makeHalo(4, COLOURS.beacon, 0.7), rig: paintRig() };
  if (art.words !== pose.words) Object.assign(art, { words: pose.words, cloth: paintCloth(pose.words) });
  const { ms, still } = pose, long = still || Math.floor(ms / PROP_MS) % 2 === 0, lit = still || ms % BEACON_EVERY < BEACON_MS;
  put(art.planes[(long ? 0 : 2) + (lit ? 1 : 0)], 0, 0, SIZE.w, SIZE.h, 0, -TOW);
  if (lit) {
    const { c, r } = art.halo;
    put(c, 0, 0, c.width, c.height, FIN.x - r, FIN.y - TOW - r, 'lighter');
  }
  put(art.rig, 0, 0, ROPE + 1, CLOTH_H, SIZE.w, -HALF);
  const from = SIZE.w + ROPE + 1, w = art.cloth.width;
  if (still) put(art.cloth, 0, 0, w, CLOTH_H, from, -HALF);
  else for (let c = 0; c < w; c++) put(art.cloth, c, 0, 1, CLOTH_H, from + c, liftAt(c, ms) - HALF);
}
